/**
 * Живая лента Пространства на стороне оболочки.
 *
 * Сервер давно рассказывает о новых Сообщениях, новых Каналах и правках Раскладки потоком
 * `/spaces/<пространство>/stream`, но слушать его было некому: экран узнавал новости только
 * при перезагрузке. Здесь один поток на всё окно — сколько бы Блоков ни подписалось, ниточка
 * к серверу остаётся одна, а закрывается она, когда ушёл последний слушатель.
 */

export type LiveType =
  | 'message.created'
  | 'message.edited'
  | 'message.removed'
  | 'channel.created'
  | 'layout.changed';

export interface LiveEvent {
  readonly type: LiveType;
  readonly payload: Record<string, unknown>;
}

type Listener = (event: LiveEvent) => void;

const listeners = new Set<Listener>();
let source: EventSource | null = null;
let openFor = '';

// Каждый вид новости подписывается отдельно: не назови мы здесь правку и уборку —
// поток бы их приносил, а слушать их было бы некому, и лента молча отставала бы от жизни.
const TYPES: readonly LiveType[] = [
  'message.created',
  'message.edited',
  'message.removed',
  'channel.created',
  'layout.changed',
];

const deliver = (type: LiveType, raw: string): void => {
  let payload: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(raw);
    // Ждём именно опись «поле — значение». Число, строка или список — не наше событие:
    // пропусти мы их с пустой описью, слушатель принял бы чужую новость за свою.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
    payload = parsed as Record<string, unknown>;
  } catch {
    // Несмышлёное сообщение не должно рвать поток: пропускаем и живём дальше.
    return;
  }
  for (const listener of [...listeners]) {
    // Один упавший слушатель не лишает новостей остальных.
    try {
      listener({ type, payload });
    } catch {
      /* пусто */
    }
  }
};

const open = (spaceSlug: string): void => {
  if (source && openFor === spaceSlug) return;
  close();
  openFor = spaceSlug;
  // Свои куки браузер шлёт сам: поток с того же адреса, что и остальное.
  source = new EventSource(`/spaces/${encodeURIComponent(spaceSlug)}/stream`);
  for (const type of TYPES) {
    source.addEventListener(type, (event) => deliver(type, (event as MessageEvent<string>).data));
  }
  // Оборвалась связь — браузер переподключается сам, отдельного присмотра не нужно.
};

const close = (): void => {
  source?.close();
  source = null;
  openFor = '';
};

/**
 * Подписаться на живую ленту. Возвращает отписку — её обязан позвать тот, кто подписался,
 * иначе поток останется висеть после ухода с экрана.
 */
export function listenLive(spaceSlug: string, listener: Listener): () => void {
  listeners.add(listener);
  open(spaceSlug);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) close();
  };
}
